import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const MeetingPaceRow = ({
  name, pace, ratings, color
}) => {
  // Find the rating that matches the pace value for custom attributes,
  // rather than assuming linear indexing
  const matchingRating = ratings?.find((rating) => rating.rating_value === pace)
    || ratings?.[pace - 1];

  return (
    <TableRow>
      <TableCell
        style={{
          color: '#000F16D9',
          fontFamily: 'Lato',
          fontWeight: 700,
          verticalAlign: 'top',
        }}
      >
        {name}
      </TableCell>
      <TableCell
        style={{
          color,
          fontFamily: 'Lato',
          fontWeight: 700,
          verticalAlign: 'top',
        }}
      >
        {pace}
      </TableCell>
      <TableCell
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          verticalAlign: 'top',
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontFamily: 'Lato',
            verticalAlign: 'top',
          }}
        >
          {matchingRating?.title || matchingRating?.rating_label || ''}
        </div>
        <div
          id="pace-rating-text"
          style={{
            color: '#0A153799',
            fontFamily: 'Lato',
            verticalAlign: 'top',
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: matchingRating?.text || matchingRating?.rating_desc || ''
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const getCustomPaceValue = (customPaceRatings, attributeId) => {
  const rating = customPaceRatings?.find((r) => r.attributeId === attributeId);
  return rating ? rating.rating_value : null;
};

export const MeetingPrintPaceSection = ({
  tableHead, meetingPace, epaCoachingText, defaultPaceColors
}) => {
  const { t } = useTranslation();
  const customPaceRatings = meetingPace?.custom_pace_ratings || [];
  const customPaceAttributes = epaCoachingText?.custom_pace_attributes || [];

  // Check if there are any actual pace ratings (not undefined values)
  const hasStandardPaceRatings = meetingPace && (
    meetingPace.perform !== undefined ||
    meetingPace.align !== undefined ||
    meetingPace.comp !== undefined ||
    meetingPace.engage !== undefined ||
    meetingPace.attribute5 !== undefined
  );

  const hasCustomPaceRatings = customPaceRatings.length > 0;

  const isData = hasStandardPaceRatings || hasCustomPaceRatings;

  const performColor = defaultPaceColors?.standard?.perform;
  const alignColor = defaultPaceColors?.standard?.align;
  const competenceColor = defaultPaceColors?.standard?.competence;
  const engagementColor = defaultPaceColors?.standard?.engagement;
  const attribute5Color = defaultPaceColors?.standard?.attribute5;

  return (
    <TableContainer component={Paper} elevation={0} style={{ fontFamily: 'Lato' }}>
      <div
        style={{
          fontSize: 16,
          color: '#000F16D9',
          fontWeight: 700,
          fontFamily: 'Lato',
          whiteSpace: 'nowrap',
          marginBottom: '10px',
        }}
      >
        {tableHead}
      </div>
      <div style={{ borderRadius: '10px', overflow: 'hidden' }}>
        <Table style={{ borderCollapse: 'collapse', border: 'none' }}>
          {isData ? (
            <TableBody
              sx={{
                backgroundColor: '#F7F9FA',
                height: '400px',
                width: '600px',
                fontSize: 16,
                '& .MuiTableCell-root': { borderBottom: 'none' },
              }}
            >
              {epaCoachingText?.perform?.enabled && (
                <MeetingPaceRow
                  name={epaCoachingText?.perform?.name}
                  pace={meetingPace?.perform}
                  ratings={epaCoachingText?.perform?.ratings}
                  color={performColor}
                />
              )}
              {epaCoachingText?.align?.enabled && (
                <MeetingPaceRow
                  name={epaCoachingText?.align?.name}
                  pace={meetingPace?.align}
                  ratings={epaCoachingText?.align?.ratings}
                  color={alignColor}
                />
              )}
              {epaCoachingText?.comp?.enabled && (
                <MeetingPaceRow
                  name={epaCoachingText?.comp?.name}
                  pace={meetingPace?.comp}
                  ratings={epaCoachingText?.comp?.ratings}
                  color={competenceColor}
                />
              )}
              {epaCoachingText?.engage?.enabled && (
                <MeetingPaceRow
                  name={epaCoachingText?.engage?.name}
                  pace={meetingPace?.engage}
                  ratings={epaCoachingText?.engage?.ratings}
                  color={engagementColor}
                />
              )}
              {epaCoachingText?.attribute5?.enabled && (
                <MeetingPaceRow
                  name={epaCoachingText?.attribute5?.name}
                  pace={meetingPace?.attribute5}
                  ratings={epaCoachingText?.attribute5?.ratings}
                  color={attribute5Color}
                />
              )}
              {/* Custom Pace Attributes */}
              {customPaceAttributes
                .filter((attr) => attr.enabled)
                .map((customAttribute, index) => {
                  const customPaceValue = getCustomPaceValue(customPaceRatings, customAttribute.id);
                  // default -> black when run out of colors
                  const attributeColor = defaultPaceColors?.custom?.[index] || '#000000';

                  return (
                    <MeetingPaceRow
                      key={`custom-attr-${customAttribute.id}`}
                      name={customAttribute.name}
                      pace={customPaceValue}
                      ratings={customAttribute.ratings}
                      color={attributeColor}
                    />
                  );
                })}
            </TableBody>
          ) : (
            <TableBody
              style={{
                height: 60,
                width: 600,
                backgroundColor: '#F7F9FA',
              }}
            >
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="left"
                  style={{
                    color: '#0A153799',
                    fontFamily: 'Lato',
                    borderBottom: 'none',
                    fontStyle: 'italic',
                  }}
                >
                  {t('No ratings provided')}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </div>
    </TableContainer>
  );
};
